import React from 'react';
import { ShieldCheck, ArrowRight, Clock, RadioTower } from 'lucide-react';
import { formatAktivitaet } from '../../services/moduleActivity';
import { begehungsKnopfTexte, type BegehungsKnopfZustand } from './projectHubLogic';

/**
 * Der Begehungs-Knopf ganz oben im Projekt-Hub — nur auf dem Handy.
 *
 * Auf dem Bau ist die Begehung der einzige Grund, das Projekt zu oeffnen.
 * Im Raster stand sie bewusst unten rechts (Schreibtisch-Logik: erst die
 * einmaligen Dokumente, dann das Laufende); auf dem Handy hiess das jedes
 * Mal Scrollen an Ablage und Einweisung vorbei. Der Knopf ersetzt dort die
 * Kachel — beide Raster blenden sie auf dem Handy aus, damit die Begehung
 * nicht zweimal auf dem Schirm steht.
 *
 * Die Aufschrift sagt, was der Tipp tut (Zustand in projectHubLogic
 * resolveBegehungsKnopf): offener Entwurf → direkt ins Modul; sonst der
 * Begehungsdialog. Beim Entwurf fuehrt „Weitere Optionen" in den Dialog
 * (Datum/Uhrzeit, Sperre loesen) — auf dem Handy der einzige Weg dorthin.
 * Der Entwurf nennt sein Datum, denn der Versand bucht darauf; ist es nicht
 * von heute, hebt sich die Zeile ab.
 */
interface Props {
    zustand: BegehungsKnopfZustand;
    offeneFreimeldungen?: number;
    /** Heute, lokal als YYYY-MM-DD. */
    heute?: string;
    lastActivity?: string | null;
    /** Begehungsdialog (Starten, Ansehen, Begruenden). */
    onOpen: () => void;
    /** Offenen Entwurf direkt im Modul weiterbearbeiten. */
    onFortsetzen: () => void;
}

export const MobileBegehungButton: React.FC<Props> = ({ zustand, offeneFreimeldungen = 0, heute, lastActivity, onOpen, onFortsetzen }) => {
    const letzte = formatAktivitaet(lastActivity);
    const texte = begehungsKnopfTexte(zustand, offeneFreimeldungen, heute);

    if (zustand.art === 'deaktiviert') {
        return (
            <div className="w-full bg-slate-50 border border-slate-200 rounded-2xl px-4 py-3.5 flex items-center gap-3 opacity-60 cursor-not-allowed" title="Begehungen sind für dieses Projekt deaktiviert.">
                <div className="bg-slate-200 w-11 h-11 rounded-xl flex items-center justify-center shrink-0"><ShieldCheck className="w-5 h-5 text-slate-400" /></div>
                <div className="min-w-0 flex-1">
                    <p className="font-bold text-slate-600 text-sm">{texte.titel}</p>
                    <p className="text-[11px] text-slate-400">{texte.hinweis}</p>
                </div>
            </div>
        );
    }

    const fortsetzen = zustand.art === 'fortsetzen';
    return (
        <div>
            <button
                type="button"
                onClick={fortsetzen ? onFortsetzen : onOpen}
                className="w-full bg-gradient-to-b from-emerald-500 to-emerald-600 text-white rounded-2xl px-4 py-3.5 flex items-center gap-3 shadow-lg shadow-emerald-200/60 ring-1 ring-inset ring-white/20 active:scale-[0.98] transition-transform text-left"
            >
                <div className="bg-white/20 w-11 h-11 rounded-xl flex items-center justify-center shrink-0"><ShieldCheck className="w-6 h-6" /></div>
                <div className="min-w-0 flex-1">
                    <p className="font-bold text-base leading-tight">{texte.titel}</p>
                    {texte.hinweis && (
                        <p className={`text-[11px] mt-0.5 truncate ${texte.hinweisBetont ? 'text-white font-semibold' : 'text-emerald-50 font-medium'}`}>{texte.hinweis}</p>
                    )}
                    {texte.freimeldungen && (
                        <p className="text-[11px] text-white font-semibold flex items-center gap-1 mt-0.5">
                            <RadioTower className="w-3 h-3 shrink-0" />
                            <span className="min-w-0 truncate">{texte.freimeldungen}</span>
                        </p>
                    )}
                    <p className="text-[11px] text-emerald-100/90 flex items-center gap-1 mt-1">
                        <Clock className="w-3 h-3 shrink-0" />
                        <span>{letzte ? `Letzte Begehung ${letzte}` : 'Noch keine Begehung'}</span>
                    </p>
                </div>
                <ArrowRight className="w-5 h-5 shrink-0" />
            </button>
            {fortsetzen && (
                <button
                    type="button"
                    onClick={onOpen}
                    className="mt-1 w-full min-h-11 rounded-xl flex items-center justify-center text-sm font-semibold text-emerald-700 active:bg-emerald-50 transition-colors"
                >
                    Weitere Optionen
                </button>
            )}
        </div>
    );
};
